import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { AutoComplete, Button, DatePicker, Input, Select, Tooltip } from 'antd'
import { CloseOutlined, PlusOutlined } from '@ant-design/icons'
import type { McpCatalog, McpCatalogEntry, McpStepQuery } from '@/types/mcp'
import { useMcpCatalogValues } from '../hooks'
import {
  asAtDay,
  DATE_GROUPS,
  dateChoiceKey,
  dateLabel,
  filterField,
  isListOperator,
  OPERATOR_LABELS,
  operatorsFor,
  parseDateChoiceKey,
  parseFilterValue,
  queryDate,
  queryDimensions,
  queryFilters,
  queryMeasures,
  querySort,
  withArg,
  withDate,
  withFields,
  type FilterField,
  type QueryFilter,
  type SortDir,
} from './query'
import { matchesSearch } from './text'

export type EarlierQueryStep = { index: number; id?: string; dimensions: string[] }

type StepQueryEditorProps = {
  clientId: string
  stepNumber: number
  catalog: McpCatalog | undefined
  catalogLoading: boolean
  entries: Map<string, McpCatalogEntry>
  /** The Get data steps above this one, which a filter can take values from. */
  earlierSteps: EarlierQueryStep[]
  /** Gives that step an id if it has none, and returns it. */
  onTakeValuesFrom: (stepIndex: number) => string
  value?: McpStepQuery
  onChange?: (next: McpStepQuery) => void
}

const DATE_FIELD = 'date'
const SPECIFIC_DATE = 'specificDate'
const CATALOG_SEARCH_MAX = 100
const FROM_STEP = 'from:'
const fieldKey = (field: FilterField) => `${field.kind}:${field.id}`

function parseFieldKey(key: string): FilterField | null {
  const [kind, ...rest] = key.split(':')
  const id = rest.join(':')
  return (kind === 'dimension' || kind === 'measure') && id ? { kind, id } : null
}

function bySection(entries: McpCatalogEntry[]) {
  const sections = new Map<string, McpCatalogEntry[]>()
  for (const entry of entries) sections.set(entry.section, [...(sections.get(entry.section) ?? []), entry])
  return [...sections]
}

/** Keys for the filter rows that stay with a row when one above it is removed, so each row keeps its own search. */
function useRowKeys(count: number) {
  const [state, setState] = useState(() => ({ keys: Array.from({ length: count }, (_, at) => at), next: count }))
  let { keys } = state
  if (keys.length !== count) {
    const added = Math.max(0, count - keys.length)
    keys = [...keys.slice(0, count), ...Array.from({ length: added }, (_, at) => state.next + at)]
    setState({ keys, next: state.next + added })
  }
  const remove = (index: number) => setState((current) => ({ ...current, keys: current.keys.filter((_, at) => at !== index) }))
  return { keys, remove }
}

const catalogMatch = (entries: Map<string, McpCatalogEntry>, input: string, id: unknown) => {
  const entry = typeof id === 'string' ? entries.get(id) : undefined
  return entry ? matchesSearch(entry, input) : false
}

export default function StepQueryEditor({
  clientId,
  stepNumber,
  catalog,
  catalogLoading,
  entries,
  earlierSteps,
  onTakeValuesFrom,
  value,
  onChange,
}: StepQueryEditorProps) {
  const query = useMemo(() => value ?? {}, [value])
  const [addingDate, setAddingDate] = useState(false)
  const [pickingDay, setPickingDay] = useState(false)
  const measures = queryMeasures(query)
  const dimensions = queryDimensions(query)
  const filters = queryFilters(query)
  const rows = useRowKeys(filters.length)
  const date = queryDate(query)
  const sort = querySort(query)
  const label = (id: string) => entries.get(id)?.label ?? id
  const change = (next: McpStepQuery) => onChange?.(next)
  const setFilters = (next: QueryFilter[]) => change(withArg(query, 'filters', next))

  const section = measures.map((id) => entries.get(id)?.section).find(Boolean)
  const measureOptions = bySection(catalog?.measures ?? []).map(([name, list]) => {
    const locked = Boolean(section) && name !== section
    return {
      label: locked ? (
        <span>
          {name}
          <span className="block text-xs font-normal text-gray-500">
            A step keeps to one section. Use another step for {name}.
          </span>
        </span>
      ) : (
        name
      ),
      title: name,
      options: list.map((entry) => ({ value: entry.id, label: entry.label, disabled: locked && !measures.includes(entry.id) })),
    }
  })
  const dimensionOptions = bySection(catalog?.dimensions ?? []).map(([name, list]) => ({
    label: name,
    title: name,
    options: list.map((entry) => ({ value: entry.id, label: entry.label })),
  }))

  const showDate = Boolean(date) || addingDate
  const fieldOptions = [
    { value: DATE_FIELD, label: 'Date range', disabled: showDate },
    ...dimensionOptions.map((group) => ({
      ...group,
      options: group.options.map((option) => ({ ...option, value: `dimension:${option.value}` })),
    })),
    ...(measures.length
      ? [{ label: 'Measures', title: 'Measures', options: measures.map((id) => ({ value: `measure:${id}`, label: label(id) })) }]
      : []),
  ]
  const filterFieldOption = (input: string, option?: { value?: unknown; label?: unknown }) =>
    typeof option?.label === 'string' && option.label.toLowerCase().includes(input.trim().toLowerCase())

  const pickField = (index: number | null, key: string) => {
    const rest = index === null ? filters : filters.filter((_, at) => at !== index)
    if (key === DATE_FIELD) {
      if (index !== null) rows.remove(index)
      setFilters(rest)
      setAddingDate(true)
      return
    }
    const field = parseFieldKey(key)
    if (!field) return
    const filter: QueryFilter = { [field.kind]: field.id, operator: field.kind === 'measure' ? 'gt' : 'equals' }
    const next = index === null ? [...rest, filter] : [...rest.slice(0, index), filter, ...rest.slice(index)]
    if (index === null) {
      setAddingDate(false)
      setPickingDay(false)
      change(withArg(withDate(query, null), 'filters', next))
    } else {
      setFilters(next)
    }
  }

  const changeOperator = (index: number, operator: string) => {
    const filter = filters[index] ?? {}
    const { valuesFrom, value: current, ...rest } = filter
    const values = (Array.isArray(current) ? current : [current]).filter((item) => item !== undefined && item !== '')
    const next: QueryFilter = { ...rest, operator }
    if (valuesFrom && isListOperator(operator)) next['valuesFrom'] = valuesFrom
    else if (isListOperator(operator)) next['value'] = values
    else if (values[0] !== undefined) next['value'] = values[0]
    setFilters(filters.map((item, at) => (at === index ? next : item)))
  }

  const changeFilter = (index: number, next: QueryFilter) => setFilters(filters.map((item, at) => (at === index ? next : item)))

  const sortOnMeasure = sort ? measures.includes(sort.by) : true
  const sortDirections: { value: SortDir; label: string }[] = sortOnMeasure
    ? [
        { value: 'desc', label: 'Biggest first' },
        { value: 'asc', label: 'Smallest first' },
      ]
    : [
        { value: 'asc', label: 'A to Z' },
        { value: 'desc', label: 'Z to A' },
      ]
  const pickSortField = (by: string | undefined) => {
    if (!by) return change(withArg(query, 'sort', undefined))
    const onMeasure = measures.includes(by)
    const dir = sort && sortOnMeasure === onMeasure ? sort.dir : onMeasure ? 'desc' : 'asc'
    change(withArg(query, 'sort', { by, dir }))
  }

  const day = asAtDay(date)
  const showDayPicker = pickingDay || day !== null
  const dateKey = showDayPicker ? SPECIFIC_DATE : date ? dateChoiceKey(date) : undefined
  const dateOptions = [
    ...DATE_GROUPS.map((group) => ({
      label: group.label,
      title: group.label,
      options: [
        ...group.choices.map((choice) => ({ value: dateChoiceKey(choice), label: dateLabel(choice) })),
        ...(group.choices.some((choice) => choice.kind === 'asAt') ? [{ value: SPECIFIC_DATE, label: 'As at a specific date' }] : []),
      ],
    })),
    ...(date && !showDayPicker && !DATE_GROUPS.some((group) => group.choices.some((choice) => dateChoiceKey(choice) === dateKey))
      ? [{ label: 'Other', title: 'Other', options: [{ value: dateChoiceKey(date), label: dateLabel(date) }] }]
      : []),
  ]
  const pickDateOption = (key: string) => {
    const specific = key === SPECIFIC_DATE
    setPickingDay(specific)
    setAddingDate(specific)
    change(withDate(query, specific ? null : parseDateChoiceKey(key)))
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 md:grid-cols-2">
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
          Measures
          <Select
            mode="multiple"
            value={measures}
            placeholder="Choose measures"
            aria-label={`Step ${stepNumber} measures`}
            options={measureOptions}
            filterOption={(input, option) => catalogMatch(entries, input, option && 'value' in option ? option.value : undefined)}
            loading={catalogLoading}
            onChange={(ids: string[]) => change(withFields(query, 'measures', ids))}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
          Group by
          <Select
            mode="multiple"
            value={dimensions}
            placeholder="Choose fields"
            aria-label={`Step ${stepNumber} group by`}
            options={dimensionOptions}
            filterOption={(input, option) => catalogMatch(entries, input, option && 'value' in option ? option.value : undefined)}
            loading={catalogLoading}
            onChange={(ids: string[]) => change(withFields(query, 'dimensions', ids))}
          />
        </label>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-gray-700">Filters</span>
        {filters.map((filter, index) => {
          const field = filterField(filter)
          const entry = field ? entries.get(field.id) : undefined
          const operator = String(filter['operator'] ?? '')
          return (
            <div key={rows.keys[index] ?? `extra-${index}`} className="grid grid-cols-[170px_140px_minmax(0,1fr)_32px] items-center gap-1.5">
              <Select
                showSearch
                value={field ? fieldKey(field) : undefined}
                placeholder="Filter on"
                aria-label={`Step ${stepNumber} filter ${index + 1} field`}
                options={fieldOptions}
                filterOption={filterFieldOption}
                onChange={(key: string) => pickField(index, key)}
              />
              <Select
                value={operator || undefined}
                aria-label={`Step ${stepNumber} filter ${index + 1} operator`}
                disabled={!field}
                options={operatorsFor(field, entry).map((op): { value: string; label: string } => ({ value: op, label: OPERATOR_LABELS[op] }))}
                onChange={(op: string) => changeOperator(index, op)}
              />
              <FilterValue
                key={field ? fieldKey(field) : 'none'}
                clientId={clientId}
                label={`Step ${stepNumber} filter ${index + 1} value`}
                filter={filter}
                field={field}
                fieldLabel={field ? label(field.id) : ''}
                earlierSteps={earlierSteps}
                onTakeValuesFrom={onTakeValuesFrom}
                onChange={(next) => changeFilter(index, next)}
              />
              <Tooltip title="Remove filter">
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  aria-label={`Remove step ${stepNumber} filter ${index + 1}`}
                  onClick={() => {
                    rows.remove(index)
                    setFilters(filters.filter((_, at) => at !== index))
                  }}
                />
              </Tooltip>
            </div>
          )
        })}
        {showDate && (
          <div className="grid grid-cols-[170px_140px_minmax(0,1fr)_32px] items-center gap-1.5">
            <Select
              showSearch
              value={DATE_FIELD}
              aria-label={`Step ${stepNumber} date filter field`}
              options={fieldOptions}
              filterOption={filterFieldOption}
              onChange={(key: string) => key !== DATE_FIELD && pickField(null, key)}
            />
            <Select value="is" disabled aria-label={`Step ${stepNumber} date filter operator`} options={[{ value: 'is', label: 'is' }]} />
            <div className="flex min-w-0 gap-1.5">
              <Select
                showSearch
                className="min-w-0 flex-1"
                value={dateKey}
                placeholder="Choose a date range"
                aria-label={`Step ${stepNumber} date range`}
                options={dateOptions}
                optionFilterProp="label"
                onChange={pickDateOption}
              />
              {showDayPicker && (
                <DatePicker
                  className="min-w-0 flex-1"
                  format="DD/MM/YYYY"
                  allowClear={false}
                  value={day ? dayjs(day) : null}
                  aria-label={`Step ${stepNumber} as-at date`}
                  onChange={(picked) => picked && change(withDate(query, { kind: 'asAt', value: picked.format('YYYY-MM-DD') }))}
                />
              )}
            </div>
            <Tooltip title="Remove filter">
              <Button
                type="text"
                icon={<CloseOutlined />}
                aria-label={`Remove step ${stepNumber} date filter`}
                onClick={() => {
                  setAddingDate(false)
                  setPickingDay(false)
                  change(withDate(query, null))
                }}
              />
            </Tooltip>
          </div>
        )}
        <Button
          type="link"
          size="small"
          icon={<PlusOutlined />}
          className="!self-start !px-0"
          onClick={() => setFilters([...filters, { operator: 'equals' }])}
        >
          Add a filter
        </Button>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-gray-700">Sort</span>
        <div className="flex flex-wrap gap-1.5">
          <Select
            allowClear
            value={sort?.by}
            placeholder="None"
            className="w-52"
            aria-label={`Step ${stepNumber} sort by`}
            options={[
              { label: 'Measures', title: 'Measures', options: measures.map((id) => ({ value: id, label: label(id) })) },
              { label: 'Group by', title: 'Group by', options: dimensions.map((id) => ({ value: id, label: label(id) })) },
            ].filter((group) => group.options.length > 0)}
            onChange={(by: string | undefined) => pickSortField(by)}
          />
          <Select
            value={sort?.dir}
            placeholder="Direction"
            className="w-40"
            disabled={!sort}
            aria-label={`Step ${stepNumber} sort direction`}
            options={sortDirections}
            onChange={(dir: SortDir) => sort && change(withArg(query, 'sort', { by: sort.by, dir }))}
          />
        </div>
      </div>
    </div>
  )
}

type FilterValueProps = {
  clientId: string
  label: string
  filter: QueryFilter
  field: FilterField | null
  fieldLabel: string
  earlierSteps: EarlierQueryStep[]
  onTakeValuesFrom: (stepIndex: number) => string
  onChange: (next: QueryFilter) => void
}

function FilterValue({ clientId, label, filter, field, fieldLabel, earlierSteps, onTakeValuesFrom, onChange }: FilterValueProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(search.trim().slice(0, CATALOG_SEARCH_MAX)), 250)
    return () => window.clearTimeout(timer)
  }, [search])
  const dimension = field?.kind === 'dimension' ? field.id : ''
  const values = useMcpCatalogValues(clientId, dimension, debounced, open)
  const operator = filter['operator']
  const { value: _value, valuesFrom: _from, ...base } = filter

  if (!field) return <Input disabled aria-label={label} />

  if (field.kind === 'measure') {
    return (
      <Input
        aria-label={label}
        placeholder="A number"
        value={filter['value'] === undefined ? '' : String(filter['value'])}
        onChange={(event) => onChange({ ...base, value: parseFilterValue(event.target.value) })}
      />
    )
  }

  const valueOptions = (values.data?.values ?? []).map((item) => ({ value: item, label: item }))
  const sources = earlierSteps.filter((step) => step.dimensions.some((id) => id.toLowerCase() === field.id.toLowerCase()))
  const stepOptions = sources.map((step) => ({
    value: `${FROM_STEP}${step.index}`,
    label: `Step ${step.index + 1}’s ${fieldLabel}s`,
  }))
  const from = typeof filter['valuesFrom'] === 'string' ? filter['valuesFrom'] : ''
  const isStepOption = (key: string) => stepOptions.some((option) => option.value === key)
  const takeFrom = (key: string) => {
    if (!isStepOption(key)) return
    onChange({ ...base, operator: isListOperator(operator) ? operator : 'in', valuesFrom: onTakeValuesFrom(Number(key.slice(FROM_STEP.length))) })
  }
  const notFound = values.isFetching ? 'Loading…' : values.isError ? 'Couldn’t load the values. Type one.' : 'Type a value.'
  const truncatedNote = values.data?.truncated ? 'Showing the first 50. Type more to narrow it.' : undefined

  if (from) {
    const source = sources.find((step) => step.id === from)
    return (
      <Select
        allowClear
        aria-label={label}
        labelRender={(selected) => <span className="font-medium text-brand-700">{selected.label}</span>}
        value={source ? `${FROM_STEP}${source.index}` : from}
        options={source ? stepOptions : [{ value: from, label: `The “${from}” step’s ${fieldLabel}s` }, ...stepOptions]}
        onChange={(key: string | undefined) => (key ? takeFrom(key) : onChange({ ...base, value: [] }))}
      />
    )
  }

  if (isListOperator(operator)) {
    const current = Array.isArray(filter['value']) ? filter['value'].map(String) : filter['value'] ? [String(filter['value'])] : []
    return (
      <Select
        mode="tags"
        aria-label={label}
        placeholder="Type or choose values"
        value={current}
        searchValue={search}
        onSearch={setSearch}
        onOpenChange={setOpen}
        filterOption={false}
        notFoundContent={notFound}
        popupRender={(menu) => (
          <>
            {menu}
            {truncatedNote && <div className="px-3 py-1 text-xs text-gray-500">{truncatedNote}</div>}
          </>
        )}
        options={[
          { label: 'Values', title: 'Values', options: valueOptions },
          ...(stepOptions.length ? [{ label: 'From an earlier step', title: 'From an earlier step', options: stepOptions }] : []),
        ].filter((group) => group.options.length > 0)}
        onChange={(next: string[]) => {
          const step = next.find(isStepOption)
          if (step) return takeFrom(step)
          setSearch('')
          onChange({ ...base, value: next })
        }}
      />
    )
  }

  return (
    <AutoComplete
      aria-label={label}
      placeholder="Type or choose a value"
      value={filter['value'] === undefined ? '' : String(filter['value'])}
      options={valueOptions}
      onOpenChange={setOpen}
      onSearch={setSearch}
      notFoundContent={open ? notFound : undefined}
      onChange={(text: string) => onChange({ ...base, value: text })}
    />
  )
}
